/**
 * Application Navigation Component
 *
 * Side navigation or horizontal nav with main app sections
 * Updated: Added compact SKI timeline to navigation bar
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useHistory } from 'react-router-dom';
import { ROUTES, USER_ROLES } from '../../config/constants';
import CompactSKITimeline from '../Dashboard/CompactSKITimeline';
import HelpModal from '../Help/HelpModal';
import './AppNav.css';

const AppNav = ({ userRole }) => {
  const history = useHistory();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowHelpDropdown(false);
      }
    };

    if (showHelpDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelpDropdown]);

  const handleUserGuideClick = () => {
    setShowHelpDropdown(false);
    history.push(ROUTES.HELP);
  };

  const handleTechOverviewClick = () => {
    setShowHelpDropdown(false);
    history.push(ROUTES.HELP + '#tech');
    setTimeout(() => {
      const el = document.getElementById('tech');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  // Help documents configuration (PDFs removed — content moved to in-app User Guide)
  const helpDocuments = [];

  const handleHelpDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setShowHelpModal(true);
    setShowHelpDropdown(false);
  };

  const isPrivileged = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.DEVELOPER;

  const navItems = [
    {
      path: ROUTES.DASHBOARD,
      label: 'Dashboard',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
        </svg>
      ),
    },
    {
      path: ROUTES.REPORTS,
      label: 'Reporting',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
      ),
    },
  ];

  if (isPrivileged) {
    navItems.push({
      path: ROUTES.PRODUCT_HIERARCHY,
      label: 'Product Hierarchy',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
        </svg>
      ),
    });
    navItems.push({
      path: ROUTES.ADMIN,
      label: 'Admin',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      ),
    });
  }

  return (
    <>
      <nav className="app-nav">
        <div className="app-nav__container">
          <div className="app-nav__links">
            {navItems.map((item, index) => {
              // If item has a path, render NavLink
              if (item.path) {
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className="app-nav__link"
                    activeClassName="app-nav__link--active"
                  >
                    <span className="app-nav__icon">{item.icon}</span>
                    <span className="app-nav__label">{item.label}</span>
                  </NavLink>
                );
              }
              return null;
            })}

            {/* Help Dropdown */}
            <div className="app-nav__help-dropdown" ref={dropdownRef}>
              <button
                className="app-nav__button"
                onClick={() => setShowHelpDropdown(!showHelpDropdown)}
                type="button"
              >
                <span className="app-nav__icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </span>
                <span className="app-nav__label">Help</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`app-nav__chevron ${showHelpDropdown ? 'open' : ''}`}
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {showHelpDropdown && (
                <div className="app-nav__help-menu">
                  <button
                    className="app-nav__help-menu-item app-nav__help-menu-item--featured"
                    onClick={handleUserGuideClick}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                    </svg>
                    User Guide
                  </button>
                  <button
                    className="app-nav__help-menu-item"
                    onClick={handleTechOverviewClick}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Product Requirements
                  </button>
                  {helpDocuments.map((doc) => (
                    <button
                      key={doc.filename}
                      className="app-nav__help-menu-item"
                      onClick={() => handleHelpDocumentClick(doc)}
                      type="button"
                    >
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {doc.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="app-nav__timeline">
            <CompactSKITimeline />
          </div>
        </div>
      </nav>

      {/* Help Modal */}
      {showHelpModal && selectedDocument && (
        <HelpModal
          documentTitle={selectedDocument.title}
          documentUrl={`http://localhost:3001/api/help/files/${selectedDocument.filename}`}
          onClose={() => {
            setShowHelpModal(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </>
  );
};

AppNav.propTypes = {
  userRole: PropTypes.string,
};

export default AppNav;
